import {inject, injectable} from 'inversify';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import type {
    ApiSubscriptionCancelResponse,
    ApiSubscriptionPlansResponse,
    ApiSubscriptionRequest,
    ApiSubscriptionResponse,
    BillingExport,
    BillingGrantsResponse,
    BillingHistoryResponse,
    BillingOverview,
    BillingUsageResponse,
    CreditGrantRow,
    PaymentEventType,
    TopupPacksResponse,
    TopupRequest,
    TopupResponse,
    WebsitePlanResponse,
} from 'xconvert-web-shared';

import type {RecentActivityRow} from '../components/portal/overview/recent-activity-list.component';
import type {SparkDay} from '../components/portal/overview/usage-sparkline.component';
import type {CancelReason} from '../components/portal/plans/cancel-modal.component';
import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';
import {LoginService} from './login.service';

/**
 * A merged "ledger" row for the Credits-page "Recent grants & usage"
 * card (Task 5.2). Positive `delta` = credit granted; negative = debit.
 */
export interface LedgerRow {
    type: 'grant' | 'debit';
    label: string;
    date: string;
    delta: number;
}

/**
 * Human label for a `GrantType` enum value. Falls back to the raw enum
 * string for forward-compat with newer backend values.
 */
function grantTypeLabel(type: string): string {
    switch (type) {
        case 'PAID_TOPUP': return 'Credit top-up';
        case 'SUBSCRIPTION_ALLOTMENT': return 'Subscription grant';
        case 'ADMIN_PROMO': return 'Admin promo';
        case 'REFUND_REVERSAL': return 'Refund reversal';
        default: return type;
    }
}

/**
 * Format an absolute timestamp into a short "happened X ago" string for
 * the Overview's Recent activity rows. Falls back to a short month/day
 * label once an event is older than a week (still inside the 7-day
 * window in normal flow, but resilient to clock skew / late events).
 */
function humanRelative(ts: number | string): string {
    const t = typeof ts === 'string' ? Date.parse(ts) : ts;
    const diff = Date.now() - t;
    const h = Math.floor(diff / 3_600_000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const d = Math.floor(h / 24);
    if (d === 1) return 'Yesterday';
    if (d < 7) return `${d} days ago`;
    return new Date(t).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

/**
 * Portal-side billing service per payments-redesign.md §F11 (Stream D / D1).
 *
 * Wraps the `/me/billing/*` routes exposed on professorx (Stream B). All
 * calls require an authenticated session; unauthenticated calls reject up
 * front before the network round-trip.
 *
 * Thin RPC wrapper — forwards the server's response shapes from
 * `xconvert-web-shared` (mirrors of the Kotlin DTOs) without local
 * massaging so portal components see the same field names as the
 * controller responses.
 *
 * Stream D pages consume this service via Inversify injection:
 * - D2 overview page → `overview()`
 * - D3 history page → `history(...)`
 * - D4 usage page → `usage(...)` + `grants(...)`
 * - D5 topup page → `listTopupPacks()` + `startTopup(...)`
 *
 * **Streaming.** Returns RxJS Observables (matches the rest of the portal
 * service layer). Components subscribe in their lifecycle hooks; the
 * RxJS pipeline composes nicely with the existing observable plumbing.
 */
@injectable()
export class BillingService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) {}

    /** GET /me/billing — overview snapshot (entitlements + balances). */
    overview(): Observable<BillingOverview> {
        this.requireLogin();
        return this.http.get<BillingOverview>(this.apiConfig.getMeBillingEndpoint())
            .pipe(ResponsePipe);
    }

    /**
     * GET /me/billing/history — paginated payment events.
     *
     * `limit` is clamped server-side (1..200; default 50). Pass an
     * optional `eventType` to filter; an unknown value is silently
     * ignored server-side rather than 400'd, keeping the UI robust to
     * client/server enum drift.
     */
    history(opts: {
        limit?: number;
        skip?: number;
        eventType?: PaymentEventType;
        /** ISO-8601 instant — events with `occurredAt >= from` only. */
        from?: string;
        /** ISO-8601 instant — events with `occurredAt < to` only (half-open). */
        to?: string;
        /**
         * `'INVOICE'` restricts to invoice-grade rows (subscription payments
         * + credit top-ups). Drives the `/portal/invoices` page. Other
         * values silently ignored server-side.
         */
        category?: 'INVOICE';
        /**
         * When true, excludes operational noise from the audit log:
         * `CHECKOUT_SESSION_EXPIRED`, `SUBSCRIPTION_STATE_CHANGED`,
         * `ADMIN_ENTITLEMENT_*`. Drives the "Show technical events" toggle
         * on `/portal/billing-history` (false = hide them, default).
         */
        hideTechnical?: boolean;
    } = {}): Observable<BillingHistoryResponse> {
        this.requireLogin();
        const params = new URLSearchParams();
        if (opts.limit !== undefined) params.set('limit', String(opts.limit));
        if (opts.skip !== undefined) params.set('skip', String(opts.skip));
        if (opts.eventType !== undefined) params.set('eventType', opts.eventType);
        if (opts.from) params.set('from', opts.from);
        if (opts.to) params.set('to', opts.to);
        if (opts.category) params.set('category', opts.category);
        if (opts.hideTechnical) params.set('hideTechnical', 'true');
        const url = this.appendQuery(this.apiConfig.getMeBillingHistoryEndpoint(), params);
        return this.http.get<BillingHistoryResponse>(url).pipe(ResponsePipe);
    }

    /**
     * GET /me/billing/usage — debit aggregations over a date window.
     *
     * Server defaults to the last 30 days when `since`/`until` are
     * omitted. ISO-8601 strings, e.g. `'2026-04-01T00:00:00Z'`.
     */
    usage(opts: {since?: string; until?: string} = {}): Observable<BillingUsageResponse> {
        this.requireLogin();
        const params = new URLSearchParams();
        if (opts.since) params.set('since', opts.since);
        if (opts.until) params.set('until', opts.until);
        const url = this.appendQuery(this.apiConfig.getMeBillingUsageEndpoint(), params);
        return this.http.get<BillingUsageResponse>(url).pipe(ResponsePipe);
    }

    /**
     * POST /me/billing/portal-session — mint a Stripe Billing Portal
     * session URL for the signed-in user. The portal page sends the
     * returnUrl so Stripe sends the user back to billing overview after
     * they update payment / cancel / view invoices in Stripe's hosted UI.
     *
     * Returns the URL string. Conflict (409 `no_stripe_customer`) is
     * surfaced as an error the caller can render as a "subscribe first"
     * hint — users who've never paid via Stripe don't have a `cus_…`.
     */
    createPortalSession(returnUrl?: string): Observable<{url: string}> {
        this.requireLogin();
        return this.http
            .post<{url: string}>(
                `${this.apiConfig.getMeBillingHistoryEndpoint().replace('/history', '/portal-session')}`,
                returnUrl ? {returnUrl} : {},
            )
            .pipe(ResponsePipe);
    }

    /** GET /me/billing/grants — grant history. */
    grants(limit = 100): Observable<BillingGrantsResponse> {
        this.requireLogin();
        const url = this.appendQuery(
            this.apiConfig.getMeBillingGrantsEndpoint(),
            new URLSearchParams({limit: String(limit)}),
        );
        return this.http.get<BillingGrantsResponse>(url).pipe(ResponsePipe);
    }

    /**
     * Recent ledger — last N entries merged from grant + debit history
     * for the Credits-page "Recent grants & usage" card (Task 5.2).
     *
     * Backend exposes `/me/billing/grants` (rows) but does NOT expose a
     * per-debit endpoint — `/me/billing/usage` is aggregated, and
     * `/me/billing/history` is payment events (subscription / topup
     * lifecycle), not per-conversion debits. The merge therefore pulls
     * the live grants endpoint and treats the debit side as empty until
     * a backend endpoint lands. Both halves are wrapped in `catchError`
     * → empty so a transient API hiccup on either feed renders the
     * empty state rather than blowing up the page.
     */
    recentLedger(args: {limit: number}): Observable<LedgerRow[]> {
        this.requireLogin();
        const limit = Math.max(1, args.limit);
        const grantsUrl = this.appendQuery(
            this.apiConfig.getMeBillingGrantsEndpoint(),
            new URLSearchParams({limit: String(limit)}),
        );
        const grants$ = this.http
            .get<BillingGrantsResponse>(grantsUrl)
            .pipe(
                ResponsePipe,
                map((resp) => resp.grants ?? []),
                catchError(() => of([] as CreditGrantRow[])),
            );
        // Debit side: no per-row endpoint exists today. Resolve as []
        // so the merge logic still produces a valid ledger list.
        const debits$ = of([] as Array<{label?: string; occurredAt?: string; amount?: number; creditsDebited?: number}>);

        return forkJoin({grants: grants$, debits: debits$}).pipe(
            map(({grants, debits}) => {
                const merged: LedgerRow[] = [
                    ...grants.map((g): LedgerRow => ({
                        type: 'grant',
                        label: grantTypeLabel(g.type),
                        date: g.grantedAt,
                        delta: g.creditsGranted,
                    })),
                    ...debits.map((d): LedgerRow => ({
                        type: 'debit',
                        label: d.label ?? 'API usage',
                        date: d.occurredAt ?? new Date(0).toISOString(),
                        delta: -Math.abs(d.creditsDebited ?? d.amount ?? 0),
                    })),
                ];
                merged.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
                return merged.slice(0, limit);
            }),
        );
    }

    /**
     * GET /me/billing/export.json — bulk JSON export for download.
     *
     * Capped at 5000 rows of each kind server-side.
     */
    exportJson(opts: {since?: string; until?: string} = {}): Observable<BillingExport> {
        this.requireLogin();
        const params = new URLSearchParams();
        if (opts.since) params.set('since', opts.since);
        if (opts.until) params.set('until', opts.until);
        const url = this.appendQuery(this.apiConfig.getMeBillingExportJsonEndpoint(), params);
        return this.http.get<BillingExport>(url).pipe(ResponsePipe);
    }

    /**
     * `GET /me/billing/export.csv`. Returns the URL the caller should
     * trigger via a browser navigation (`window.location.href = url`)
     * for the download — `<a href>` semantics, not in-memory fetch,
     * so the browser shows the standard download dialog with the
     * correct `text/csv` Content-Type from the server.
     */
    exportCsvUrl(opts: {since?: string; until?: string} = {}): string {
        this.requireLogin();
        const params = new URLSearchParams();
        if (opts.since) params.set('since', opts.since);
        if (opts.until) params.set('until', opts.until);
        return this.appendQuery(this.apiConfig.getMeBillingExportCsvEndpoint(), params);
    }

    /** GET /me/billing/topup/packs — pack catalog for the topup page. */
    listTopupPacks(): Observable<TopupPacksResponse> {
        this.requireLogin();
        return this.http
            .get<TopupPacksResponse>(this.apiConfig.getMeBillingTopupPacksEndpoint())
            .pipe(ResponsePipe);
    }

    /**
     * POST /me/billing/topup — start a Stripe Checkout session.
     *
     * Returns `{checkoutUrl, ...}`. The portal redirects the browser:
     * `window.location.href = response.checkoutUrl`. Stripe handles
     * payment, then redirects back to `success_url` / `cancel_url`
     * with a `topup=success|cancelled&session_id=...` query.
     */
    startTopup(req: TopupRequest): Observable<TopupResponse> {
        this.requireLogin();
        return this.http
            .post<TopupResponse>(this.apiConfig.getMeBillingTopupEndpoint(), req)
            .pipe(ResponsePipe);
    }

    /** GET /me/billing/api-subscription/plans — API plan catalog. */
    listApiSubscriptionPlans(): Observable<ApiSubscriptionPlansResponse> {
        this.requireLogin();
        return this.http
            .get<ApiSubscriptionPlansResponse>(this.apiConfig.getMeBillingApiSubscriptionPlansEndpoint())
            .pipe(ResponsePipe);
    }

    /** GET /me/billing/website-plans — Website plan catalog (Backer/Patron). */
    listWebsitePlans(): Observable<WebsitePlanResponse> {
        this.requireLogin();
        return this.http
            .get<WebsitePlanResponse>(this.apiConfig.getMeBillingWebsitePlansEndpoint())
            .pipe(ResponsePipe);
    }

    /**
     * POST /me/billing/api-subscription — start an API subscription via
     * Stripe Checkout (recurring). Returns 409 (handled by HTTP layer)
     * if the user already has an active API entitlement.
     */
    startApiSubscription(req: ApiSubscriptionRequest): Observable<ApiSubscriptionResponse> {
        this.requireLogin();
        return this.http
            .post<ApiSubscriptionResponse>(this.apiConfig.getMeBillingApiSubscriptionEndpoint(), req)
            .pipe(ResponsePipe);
    }

    /**
     * POST /me/billing/api-subscription/cancel — schedule cancel-at-
     * period-end. Idempotent: same response shape whether or not there
     * was an active sub.
     *
     * Optional `reason` (a UI-side `CancelReason` enum) is recorded
     * server-side for funnel analytics; null means the user declined
     * to pick one (the dropdown defaults to "— Select if you'd like —").
     * Pre-Task-11 callers that pass no argument still work — the body
     * becomes `{reason: null}` which the backend tolerates.
     */
    cancelApiSubscription(
        {reason}: {reason: CancelReason | null} = {reason: null},
    ): Observable<ApiSubscriptionCancelResponse> {
        this.requireLogin();
        return this.http
            .post<ApiSubscriptionCancelResponse>(
                this.apiConfig.getMeBillingApiSubscriptionCancelEndpoint(),
                {reason},
            )
            .pipe(ResponsePipe);
    }

    /**
     * POST /v1/api/payments/cancel — cancel the user's active Website
     * subscription (Backer/Patron). Mirrors `cancelApiSubscription` for
     * the Website tier: schedules cancel-at-period-end on Stripe (resume
     * works via the Customer Portal) or PayPal (resubscribe via a new
     * checkout). Idempotent: same response whether or not a sub exists.
     *
     * Re-uses the long-standing `/payments/cancel` endpoint already
     * wired for the legacy /product UI — no new backend surface.
     */
    cancelWebsiteSubscription(
        {reason}: {reason: CancelReason | null} = {reason: null},
    ): Observable<unknown> {
        this.requireLogin();
        return this.http
            .post<unknown>(this.apiConfig.getCancelPaymentsEndpoint(), {reason})
            .pipe(ResponsePipe);
    }

    /**
     * GET /portal/conversion-history — top-N recent conversion activity
     * for the user, narrowed to the last `daysBack` days. Powers the
     * Overview "Recent activity" card.
     *
     * Maps the server's raw `events` payload to the UI's
     * `RecentActivityRow` shape (humanised timestamp + short source
     * label + status bucket). The thin transform lives here, not in
     * the component, so the page stays presentation-only.
     */
    recentActivity(args: {limit: number; daysBack: number}): Observable<RecentActivityRow[]> {
        this.requireLogin();
        const from = new Date(Date.now() - args.daysBack * 24 * 3600 * 1000).toISOString();
        const params = new URLSearchParams();
        params.set('limit', String(args.limit));
        params.set('from', from);
        const url = this.appendQuery(this.apiConfig.getConversionHistoryEndpoint(), params);
        return this.http.get<{events: any[]}>(url).pipe(
            ResponsePipe,
            map((resp): RecentActivityRow[] => (resp.events ?? []).map((e: any): RecentActivityRow => ({
                id: String(e.id ?? e._id ?? Math.random()),
                filename: e.filename ?? e.workflowName ?? 'Conversion',
                happenedAt: humanRelative(e.timestamp),
                // API rows show the key prefix when the backend has it.
                // The merged scheduled_tasks feed doesn't carry a prefix
                // today, so fall back to the bare "API" label rather
                // than rendering a literal `***` placeholder.
                source: e.workflowSource === 'API_KEY' || e.workflowSource === 'api_key'
                    ? (e.apiKeyPrefix ? `API · ${e.apiKeyPrefix}` : 'API')
                    : 'Web upload',
                status: e.eventType === 'JOB_COMPLETED' ? 'success'
                    : e.eventType === 'BLOCKED' ? 'blocked'
                    // JOB_ABORTED covers EXPIRED + CANCELED scheduled_tasks
                    // — neither is a true failure, so render the neutral
                    // "blocked" pill (Stripe-toned amber) rather than
                    // "failed" red.
                    : e.eventType === 'JOB_ABORTED' ? 'blocked'
                    : 'failed',
            }))),
        );
    }

    /**
     * GET /me/billing/usage-7day — 7 daily usage buckets for the
     * Overview sparkline. Backend lands in Task 4.5; this wrapper
     * already targets the right endpoint so the page works as soon as
     * the controller goes live (and renders the empty-state otherwise).
     */
    usageLast7Days(): Observable<SparkDay[]> {
        this.requireLogin();
        return this.http.get<{days: Array<{date: string; credits: number}>}>(
            this.apiConfig.getUsageLast7DaysEndpoint(),
        ).pipe(
            ResponsePipe,
            map((resp): SparkDay[] => (resp.days ?? []).map((d) => ({
                label: new Date(d.date).toLocaleDateString(undefined, {weekday: 'short'}),
                credits: d.credits,
            }))),
        );
    }

    private appendQuery(base: string, params: URLSearchParams): string {
        const qs = params.toString();
        if (!qs) return base;
        return base.includes('?') ? `${base}&${qs}` : `${base}?${qs}`;
    }

    private requireLogin(): void {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('User must be logged in to access billing endpoints');
        }
    }
}
